import * as React from "react";
import {Col, ControlLabel, Form, FormControl, FormGroup} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {PromptDialog} from "../common/ui/PromptDialog";
import {reactComponentOnChange} from "../common/util/Util";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {APOLLO_CATALOG_ROOT_FOLDER} from "./model";

const LABEL_COL = 3;
const CONTROL_COL = 9;

const MESSAGES = defineMessages({
    button: {
        id: "studio.form.create",
        defaultMessage: "Create",
    },
    executingMessage: {
        id: "studio.create-folder-dialog.executing",
        defaultMessage: "Creating folder...",
    },
    successMessage: {
        id: "studio.create-folder-dialog.success-message",
        defaultMessage: "Folder successfully created",
    },
    dialogTitle: {
        id: "studio.create-folder-dialog.dialog-title",
        defaultMessage: "Create folder",
    },
    loading: {
        id: "studio.create-folder-dialog.loading",
        defaultMessage: "Loading ...",
    },
    unknownParent: {
        id: "studio.create-folder-dialog.unknown-parent",
        defaultMessage: "Unknown Parent",
    }
});

export interface CreateFolderDialogOwnProps {
    onClose: () => void;
    onSubmit: () => void;
    parentId: string;
}

type CreateFolderDialogProps = CreateFolderDialogOwnProps & InjectedIntlProps & WithApiProperties;

interface CreateFolderDialogState {
    executing: boolean;
    error: Error;
    isSuccess: boolean;
    folderName: string;
    parentFolderName: string;
}

class CreateFolderDialogComponent extends React.Component<CreateFolderDialogProps, CreateFolderDialogState> {
    private onChange = reactComponentOnChange.bind(this);

    constructor(props: CreateFolderDialogProps) {
        super(props);

        this.state = {
            executing: false,
            error: null,
            isSuccess: false,
            folderName: "",
            parentFolderName: this.props.intl.formatMessage(MESSAGES.loading)
        };
    }

    componentDidMount() {
        const {api, parentId} = this.props;
        if (parentId === null) {
            this.setState({
                ...this.state,
                parentFolderName: APOLLO_CATALOG_ROOT_FOLDER.title
            });
        } else {
            api.getDataDetails(parentId)
                .then((response) => {
                    this.setState({
                        ...this.state,
                        parentFolderName: response.data.title
                    });
                })
                .catch((error) => {
                    this.setState({
                        ...this.state,
                        error,
                        parentFolderName: this.props.intl.formatMessage(MESSAGES.unknownParent)
                    });
                });
        }
    }

    render() {
        const {intl, onClose} = this.props;

        return (
            <PromptDialog
                intl={this.props.intl}
                renderForm={this.renderForm}
                promptButtonText={intl.formatMessage(MESSAGES.button)}
                promptButtonDisabled={this.state.folderName.length === 0}
                onSubmit={this.onSubmit}
                executing={this.state.executing}
                executingMessage={intl.formatMessage(MESSAGES.executingMessage)}
                error={this.state.error}
                warnings={null}
                success={this.state.isSuccess}
                successMessage={intl.formatMessage(MESSAGES.successMessage)}
                title={intl.formatMessage(MESSAGES.dialogTitle)}
                onHide={onClose}
            />
        );
    }

    private renderForm = () => {
        const {parentFolderName} = this.state;
        return <Form horizontal onSubmit={this.onSubmit}>
            <div>
                <strong><FormattedMessage id="studio.create-folder-dialog.selected-parent"
                                          defaultMessage="Selected Parent:"/> </strong>
                {parentFolderName}
            </div>
            <br/>
            <FormGroup controlId="folderName">
                <Col componentClass={ControlLabel} sm={LABEL_COL}>
                    <FormattedMessage id="studio.create-folder-dialog.folder-name"
                                      defaultMessage="Folder name"/>
                </Col>
                <Col sm={CONTROL_COL}>
                    <FormControl name="folderName" value={this.state.folderName} onChange={this.onChange}
                                 autoFocus={true}/>
                </Col>
            </FormGroup>
        </Form>
    };

    private onSubmit = (event: any) => {
        event.preventDefault();

        const {api, parentId} = this.props;
        const {folderName} = this.state;

        Promise.resolve()
            .then(() => {
                this.setState({...this.state, executing: true});

                return api.createFolder(parentId, folderName);
            })
            .catch((error) => {
                this.setState({...this.state, executing: false, error});

                throw error;
            })
            .then(() => {
                this.setState({...this.state, executing: false});
                this.props.onSubmit();
            });
    }
}

export const CreateFolderDialog = WithApi(injectIntl(CreateFolderDialogComponent));